import { RefObject, useEffect } from 'react';
import { findFocusableElements } from './findFocusableElements';

export const useIsolatedFocusedContainer = ({
  containerRef,
  enabled = true,
  dependencies = [],
  focusFirstElement = true,
}: {
  containerRef: RefObject<HTMLElement | null>;
  enabled?: boolean;
  /**
   * An array of dependencies that will trigger container's elements to be re-evaluated.
   * For example, if the content of the container changes based on some state (e.g. tab switch).
   */
  dependencies?: any[];
  /**
   * Whether to focus the first focusable element on component mount or enable (if provided).
   */
  focusFirstElement?: boolean;
}) => {
  useEffect(() => {
    if (!containerRef.current || !enabled) return;

    const containerEl = containerRef.current;
    const { allFocusable, firstFocusable, lastFocusable } = findFocusableElements(containerRef.current);

    if (!allFocusable) return;

    if (focusFirstElement) firstFocusable?.focus();

    function handleTabKeyPress(event: KeyboardEvent) {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable?.focus();
        }
        return;
      }

      if (document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable?.focus();
      }
    }

    containerEl.addEventListener('keydown', handleTabKeyPress);

    return () => {
      containerEl.removeEventListener('keydown', handleTabKeyPress);
    };
  }, [containerRef, enabled, focusFirstElement, ...dependencies]);
};
